import React from 'react';
import PropTypes from 'prop-types';

import useComponentDidMount from 'Hooks/useComponentDidMount';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import fr from 'facet-rest';

const StripeSetupContext = React.createContext(undefined);

// Actions
export const INITIALIZE_STARTED = 'initialize-started';
export const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
export const INITIALIZE_FAILED = 'initialize-failed';

export const STRIPE_CREATE_TOKEN_STARTED = 'stripe-create-token-started';
export const STRIPE_CREATE_TOKEN_FAILED = 'stripe-create-token-failed';

export const STRIPE_TOKEN_CREATED_AND_START_PROCESSING = 'process-started';
export const PROCESS_SUCCEEDED = 'process-succeeded';
export const PROCESS_FAILED = 'process-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    isProcessing: false,
    paymentMethodProcessed: false,
    processingError: null,
    stripeErrorCode: null,
    stripeErrorType: null,
    stripeErrorMessage: null,
    isCreatingStripeToken: false,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case STRIPE_TOKEN_CREATED_AND_START_PROCESSING: {
      return {
        ...state,
        isProcessing: true,
        isCreatingStripeToken: false,
        processingError: null,
      };
    }
    case PROCESS_SUCCEEDED: {
      return {
        ...state,
        isProcessing: false,
        paymentMethodProcessed: true,
        processingError: null,
      };
    }
    case PROCESS_FAILED: {
      return {
        ...state,
        isProcessing: false,
        isCreatingStripeToken: false,
        processingError: action.processingError,
      };
    }
    case STRIPE_CREATE_TOKEN_STARTED: {
      return {
        ...state,
        isCreatingStripeToken: true,
      };
    }
    case STRIPE_CREATE_TOKEN_FAILED: {
      return {
        ...state,
        isCreatingStripeToken: false,
        stripeErrorCode: action.stripeErrorCode,
        stripeErrorType: action.stripeErrorType,
        stripeErrorMessage: action.stripeErrorMessage,
      };
    }
    default: {
      throw new Error(`Unhandled client-agreement-context action type: ${action.type}`);
    }
  }
};

const StripeSetupProvider = ({
  tokenSubmitLink,
  children,
}) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent({
    nodeName: 'Stripe Setup',
  });

  const [state, dispatch] = React.useReducer(reducer, null, init);

  const handleProcessCreditCard = React.useCallback(async (stripe) => {
    try {
      dispatch({ type: STRIPE_CREATE_TOKEN_STARTED });

      // If we don't call catch here, the webapp doesn't properly recover from the error.
      // It gets stuck in a loading/processing state.
      const { token, error } = await stripe.createToken().catch(() => ({
        token: null,
        error: {
          message: 'Failed to process your credit card. Please try again.',
        },
      }));

      if (error) {
        dispatch({
          type: STRIPE_CREATE_TOKEN_FAILED,
          stripeErrorCode: error.code,
          stripeErrorType: error.type,
          stripeErrorMessage: error.message,
        });
        return;
      }
      dispatch({ type: STRIPE_TOKEN_CREATED_AND_START_PROCESSING });

      const data = {
        attributes: {
          token: token.id,
        },
      };

      await tokenSubmitLink
        .Post(data, { deprecatedAllowAlterOfRelation: true });

      logStandardizedEvent(categories.membershipProfileAddCreditCard, eventTypes.processCreditCard, 'stripe setup');

      dispatch({ type: PROCESS_SUCCEEDED });
    } catch (err) {
      dispatch({ type: PROCESS_FAILED, processingError: err });
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      isProcessing: state.isProcessing,
      processingError: state.processingError,
      paymentMethodProcessed: state.paymentMethodProcessed,
      stripeErrorCode: state.stripeErrorCode,
      stripeErrorType: state.stripeErrorType,
      stripeErrorMessage: state.stripeErrorMessage,
      isCreatingStripeToken: state.isCreatingStripeToken,
      hasProcessingError: state.processingError || !!state.stripeErrorCode,
      handleProcessCreditCard,
    };
  }, [
    state,
    handleProcessCreditCard,
  ]);

  useComponentDidMount(async () => {
    try {
      dispatch({
        type: INITIALIZE_STARTED,
      });

      // Add initialization steps here if needed in the future

      dispatch({
        type: INITIALIZE_SUCCEEDED,
      });
    } catch (err) {
      dispatch({
        type: INITIALIZE_STARTED,
        initializationError: err,
      });
    }
  });

  return (
    <StripeSetupContext.Provider value={memoizedValue}>
      {children}
    </StripeSetupContext.Provider>
  );
};

// Use useStripeSetup State Context
const useStripeSetupContext = () => {
  const context = React.useContext(StripeSetupContext);
  if (context === undefined) {
    throw new Error('useStripeSetupContext must be used within a StripeSetupProvider');
  }
  return context;
};

StripeSetupProvider.propTypes = {
  tokenSubmitLink: PropTypes.instanceOf(fr.Link).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  StripeSetupProvider,
  useStripeSetupContext,
};
